import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { pencilerRoles, urbancanvasRoles } from "../config/config";
import { Limits } from "../types/limits";
import { Organization } from "../types/organizations";
import { PencilerStartProjectsStatus } from "../types/pencilerStartProjects";
import { FineGrainedPermission, Permission } from "../types/permissions";
import { Role } from "../types/roles";
import { User } from "../types/users";
import { capitalize, doesNotExist } from "../utils/utils";

export type Accounting = Record<string, any>;

export type AccountsSlice = {
  accounting: Accounting;
  limits: Limits | Record<string, never>;
  organization: Organization | Record<string, never>;
  users: User[];
  roles: Role[];
  administrators: string[];
  permissions: Permission[];
  fineGrainedPermissions: FineGrainedPermission[];
  usersByRole: unknown;
  pencilerStartProjectsStatus: PencilerStartProjectsStatus | Record<string, never>;
  invitesByRoleStatus: Record<string, unknown>;
  gettingUsers: boolean;
  getUsersError: boolean; // unused
  getUsersErrorMessage: string; // unused
  gettingInvitesByRoleStatus: boolean;
  getInvitesByRoleStatusError: boolean;
  getInvitesByRoleStatusErrorMessage: string;
  gettingPencilerStartProjectsStatus: boolean;
  getPencilerStartProjectsStatusSuccess: boolean;
  getPencilerStartProjectsStatusError: boolean;
  getPencilerStartProjectsStatusErrorMessage: string;
  gettingUsersByRole: boolean;
  getUsersByRoleSuccess: boolean; // unused
  getUsersByRoleError: boolean; // unused
  getUsersByRoleErrorMessage: string; // unused
  openInviteUserToAccountModal: boolean;
  inviteUserToAccountModalFailed: boolean;
  inviteUserToAccountModalFailedMessage: string;
  submitSucceeded: boolean;
  submittingForm: boolean;
};

const initialState: AccountsSlice = {
  accounting: {},
  limits: {},
  organization: {},
  users: [],
  roles: [],
  administrators: [],
  permissions: [],
  fineGrainedPermissions: [],
  usersByRole: {},
  pencilerStartProjectsStatus: {},
  invitesByRoleStatus: {},
  gettingUsers: false,
  getUsersError: false,
  getUsersErrorMessage: "",
  gettingInvitesByRoleStatus: false,
  getInvitesByRoleStatusError: false,
  getInvitesByRoleStatusErrorMessage: "",
  gettingPencilerStartProjectsStatus: false,
  getPencilerStartProjectsStatusSuccess: false,
  getPencilerStartProjectsStatusError: false,
  getPencilerStartProjectsStatusErrorMessage: "",
  gettingUsersByRole: false,
  getUsersByRoleSuccess: false,
  getUsersByRoleError: false,
  getUsersByRoleErrorMessage: "",
  openInviteUserToAccountModal: false,
  inviteUserToAccountModalFailed: false,
  inviteUserToAccountModalFailedMessage: "",
  submitSucceeded: false,
  submittingForm: false
};

const accountsSlice = createSlice({
  name: "accounts",
  initialState,
  reducers: {
    setOrganization(state, action: PayloadAction<Organization>) {
      state.organization = action.payload;
    },
    gettingUsers(state) {
      state.gettingUsers = true;
    },
    setUsers(state, action: PayloadAction<User[]>) {
      state.gettingUsers = false;
      state.users = action.payload;
    },
    getUsersFailed(state, action: PayloadAction<string>) {
      state.users = [];
      state.getUsersError = true;
      state.getUsersErrorMessage = action.payload;
    },
    gettingUsersByRole(state) {
      state.usersByRole = [];
      state.gettingUsersByRole = true;
      state.getUsersByRoleSuccess = false;
      state.getUsersByRoleError = false;
      state.getUsersByRoleErrorMessage = "";
    },
    getUsersByRoleSuccess(state, action: PayloadAction<Record<string, unknown>>) {
      [urbancanvasRoles, pencilerRoles].forEach(roles => {
        roles.availableRoles.forEach(role => {
          if (doesNotExist(action.payload[role.code])) {
            action.payload[role.code] = [];
          }
        });
      });
      const payload = remapUsersByRole(action.payload);
      state.usersByRole = payload;
      state.gettingUsersByRole = false;
      state.getUsersByRoleSuccess = true;
      state.getUsersByRoleError = false;
      state.getUsersByRoleErrorMessage = "";
    },
    getUsersByRoleFailed(state, action: PayloadAction<string>) {
      state.usersByRole = [];
      state.gettingUsersByRole = false;
      state.getUsersByRoleSuccess = false;
      state.getUsersByRoleError = true;
      state.getUsersByRoleErrorMessage = action.payload;
    },
    setInviteUserToAccountModal(state, action: PayloadAction<boolean>) {
      state.openInviteUserToAccountModal = action.payload;
    },
    // Note: This action is not used
    startInviteUserToAccountSubmit(state) {
      state.submittingForm = true;
    },
    inviteUserToAccountFailed(state, action: PayloadAction<string>) {
      state.submittingForm = false;
      state.submitSucceeded = false;
      state.inviteUserToAccountModalFailed = true;
      state.inviteUserToAccountModalFailedMessage = action.payload;
    },
    resetInviteUserToAccountModal(state) {
      state.openInviteUserToAccountModal = false;
      state.inviteUserToAccountModalFailed = false;
      state.inviteUserToAccountModalFailedMessage = "";
      state.submitSucceeded = false;
      state.submittingForm = false;
    },
    gettingPencilerStartProjectsStatus(state) {
      state.pencilerStartProjectsStatus = {};
      state.gettingPencilerStartProjectsStatus = true;
      state.getPencilerStartProjectsStatusSuccess = false;
      state.getPencilerStartProjectsStatusError = false;
      state.getPencilerStartProjectsStatusErrorMessage = "";
    },
    getPencilerStartProjectsStatusSuccess(
      state,
      action: PayloadAction<PencilerStartProjectsStatus>
    ) {
      state.pencilerStartProjectsStatus = action.payload;
      state.gettingPencilerStartProjectsStatus = false;
      state.getPencilerStartProjectsStatusSuccess = true;
      state.getPencilerStartProjectsStatusError = false;
      state.getPencilerStartProjectsStatusErrorMessage = "";
    },
    getPencilerStartProjectsStatusFailed(state, action: PayloadAction<string>) {
      state.pencilerStartProjectsStatus = {};
      state.gettingPencilerStartProjectsStatus = false;
      state.getPencilerStartProjectsStatusSuccess = true;
      state.getPencilerStartProjectsStatusError = true;
      state.getPencilerStartProjectsStatusErrorMessage = action.payload;
    },
    gettingRoleInvitesStatus(state) {
      state.gettingInvitesByRoleStatus = true;
      state.getInvitesByRoleStatusError = false;
      state.getInvitesByRoleStatusErrorMessage = "";
    },
    setRoleInvitesStatus(state, action: PayloadAction<{ roleCode: string; status: unknown }>) {
      state.gettingInvitesByRoleStatus = false;
      state.getInvitesByRoleStatusError = false;
      state.getInvitesByRoleStatusErrorMessage = "";
      state.invitesByRoleStatus[action.payload.roleCode] = action.payload.status;
    },
    getRoleInvitesStatusFailed(state, action: PayloadAction<string>) {
      state.gettingInvitesByRoleStatus = false;
      state.getInvitesByRoleStatusError = true;
      state.getInvitesByRoleStatusErrorMessage = action.payload;
    },

    setLimits(state, action: PayloadAction<Limits>) {
      state.limits = action.payload;
    },
    setAccounting(state, action: PayloadAction<Accounting>) {
      state.accounting = action.payload;
    },
    setPermissions(state, action: PayloadAction<Permission[]>) {
      state.permissions = action.payload;
    },
    setFineGrainedPermissions(state, action: PayloadAction<FineGrainedPermission[]>) {
      state.fineGrainedPermissions = action.payload;
    },
    setOrganizationsRoles(state, action: PayloadAction<Role[]>) {
      state.roles = action.payload;
    },
    setOrganizationsAdministators(state, action: PayloadAction<string[]>) {
      state.administrators = action.payload;
    },
    setOrganizationContact(state, action: PayloadAction<any>) {
      const contacts = state.organization.contacts.filter(
        oldContacts => oldContacts.id !== action.payload.id
      );
      state.organization.contacts = [...contacts, action.payload];
    },
    removeOrganizationContact(state, action: PayloadAction<string>) {
      state.organization.contacts = state.organization.contacts.filter(
        oldContacts => oldContacts.id.toString() !== action.payload
      );
    }
  }
});

const remapUsersByRole = (usersByRole: Record<string, any>) => {
  const mappedUsersByRole: Record<string, any> = {};
  Object.keys(usersByRole).forEach(roleCode => {
    if (roleCode.startsWith(pencilerRoles.prefix) && roleCode !== "PENCILER_DEMO") {
      let mappedRoleCode = capitalize(roleCode.replace(pencilerRoles.prefix, "").toLowerCase());
      if (mappedRoleCode === "Admin") mappedRoleCode = "Administrator";
      mappedUsersByRole[mappedRoleCode] = usersByRole[roleCode];
    }
  });
  return mappedUsersByRole;
};

export const {
  setOrganization,
  gettingUsers,
  setUsers,
  getUsersFailed,
  gettingUsersByRole,
  getUsersByRoleSuccess,
  getUsersByRoleFailed,
  setInviteUserToAccountModal,
  startInviteUserToAccountSubmit,
  inviteUserToAccountFailed,
  resetInviteUserToAccountModal,
  gettingPencilerStartProjectsStatus,
  getPencilerStartProjectsStatusSuccess,
  getPencilerStartProjectsStatusFailed,
  gettingRoleInvitesStatus,
  setRoleInvitesStatus,
  getRoleInvitesStatusFailed,
  setLimits,
  setAccounting,
  setPermissions,
  setFineGrainedPermissions,
  setOrganizationsRoles,
  setOrganizationsAdministators,
  setOrganizationContact,
  removeOrganizationContact
} = accountsSlice.actions;

export default accountsSlice.reducer;
